import { useState, useEffect, useCallback, useRef } from 'react';
import { api, ApiError } from '../lib/api';

type Step = 'input' | 'waiting' | 'success' | 'timeout' | 'error';

interface PhoneVerifyModalProps {
  onClose: () => void;
  onVerified: () => void;
}

const E164_RE = /^\+[1-9]\d{1,14}$/;

export function PhoneVerifyModal({ onClose, onVerified }: PhoneVerifyModalProps) {
  const [step, setStep] = useState<Step>('input');
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [validationCode, setValidationCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cleanup = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    pollRef.current = null;
    timeoutRef.current = null;
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const startVerification = async () => {
    const trimmed = phone.trim();
    if (!trimmed) {
      setPhoneError('Phone number is required');
      return;
    }
    if (!E164_RE.test(trimmed)) {
      setPhoneError('Must be E.164 format (e.g. +15551234567)');
      return;
    }
    setPhoneError('');
    setSubmitting(true);

    try {
      const data = await api<{ callSid: string; validationCode: string; message: string }>(
        '/phone/verify',
        { method: 'POST', body: JSON.stringify({ phone: trimmed }) },
      );
      setValidationCode(data.validationCode);
      setStep('waiting');
      startPolling();
    } catch (err) {
      if (err instanceof ApiError && err.details?.errors) {
        const fieldErrors = err.details.errors as Record<string, string[]>;
        setPhoneError(fieldErrors.phone?.[0] ?? err.message);
      } else {
        setErrorMessage(err instanceof ApiError ? err.message : 'Failed to start verification');
        setStep('error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const startPolling = () => {
    pollRef.current = setInterval(async () => {
      try {
        const data = await api<{ phoneVerified: boolean; phoneNumber: string | null }>('/phone/status');
        if (data.phoneVerified) {
          cleanup();
          setStep('success');
          onVerified();
        }
      } catch {
        // Ignore polling errors, keep trying
      }
    }, 4000);

    timeoutRef.current = setTimeout(() => {
      cleanup();
      setStep('timeout');
    }, 120000);
  };

  const handleRetry = () => {
    cleanup();
    setStep('input');
    setValidationCode('');
    setErrorMessage('');
  };

  const inputStyle = (hasError: boolean) => ({
    width: '100%',
    padding: '10px 14px',
    borderRadius: 10,
    border: hasError ? '1.5px solid #C43E00' : '1.5px solid rgba(0,0,0,0.08)',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 14,
    color: '#1A1A1A',
    outline: 'none',
    background: 'white',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box' as const,
  });

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        animation: 'heroFadeIn 0.2s ease both',
      }}
      onClick={(e) => { if (e.target === e.currentTarget && step !== 'waiting') onClose(); }}
    >
      <div
        style={{
          background: '#FAFAF9',
          borderRadius: 20,
          padding: '32px 36px',
          width: 420,
          maxWidth: '90vw',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          animation: 'cardSlideUp 0.3s ease both',
        }}
      >
        {/* Input Step */}
        {step === 'input' && (
          <>
            <div className="font-serif font-normal mb-1" style={{ fontSize: 22, color: '#1A1A1A' }}>
              Verify your phone
            </div>
            <div className="font-sans text-sm mb-5" style={{ color: '#888' }}>
              We'll call this number to verify it. You'll need to enter a code on the keypad.
            </div>
            <div className="mb-4">
              <label className="font-sans text-xs font-medium mb-1 block" style={{ color: '#666' }}>
                Phone number (E.164)
              </label>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+15551234567"
                style={inputStyle(!!phoneError)}
                onKeyDown={(e) => { if (e.key === 'Enter') startVerification(); }}
                autoFocus
              />
              {phoneError && (
                <div className="font-sans text-xs mt-1" style={{ color: '#C43E00' }}>{phoneError}</div>
              )}
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={onClose}
                className="font-sans text-[13px] font-medium border-none cursor-pointer"
                style={{ padding: '8px 16px', borderRadius: 8, background: 'transparent', color: '#888' }}
              >
                Cancel
              </button>
              <button
                onClick={startVerification}
                disabled={submitting}
                className="font-sans text-[13px] font-semibold border-none cursor-pointer"
                style={{
                  padding: '8px 20px',
                  borderRadius: 8,
                  background: submitting ? 'rgba(212,88,42,0.5)' : '#D4582A',
                  color: 'white',
                }}
              >
                {submitting ? 'Calling...' : 'Verify Phone'}
              </button>
            </div>
          </>
        )}

        {/* Waiting Step */}
        {step === 'waiting' && (
          <div className="text-center">
            <div className="font-serif font-normal mb-2" style={{ fontSize: 22, color: '#1A1A1A' }}>
              Calling your phone
            </div>
            <div className="font-sans text-sm mb-6" style={{ color: '#888' }}>
              Answer the call and enter this code on your keypad:
            </div>
            <div
              className="font-mono mx-auto mb-6"
              style={{
                fontSize: 36,
                letterSpacing: '0.3em',
                color: '#D4582A',
                fontWeight: 700,
                padding: '16px 24px',
                borderRadius: 14,
                background: 'rgba(212,88,42,0.06)',
                border: '1.5px solid rgba(212,88,42,0.15)',
                display: 'inline-block',
              }}
            >
              {validationCode}
            </div>
            <div className="flex items-center justify-center gap-2 font-sans text-sm" style={{ color: '#999' }}>
              <span
                style={{
                  display: 'inline-block',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: '#D4582A',
                  animation: 'pulse 1.5s ease-in-out infinite',
                }}
              />
              Waiting for verification...
            </div>
          </div>
        )}

        {/* Success Step */}
        {step === 'success' && (
          <div className="text-center">
            <div
              className="mx-auto mb-4 flex items-center justify-center"
              style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: 'rgba(26,107,60,0.08)',
              }}
            >
              <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                <path d="M5 13l4 4L19 7" stroke="#1a6b3c" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="font-serif font-normal mb-2" style={{ fontSize: 22, color: '#1A1A1A' }}>
              Phone verified
            </div>
            <div className="font-sans text-sm mb-6" style={{ color: '#888' }}>
              Your phone number has been verified. You can now use voice tasks.
            </div>
            <button
              onClick={onClose}
              className="font-sans text-[13px] font-semibold border-none cursor-pointer"
              style={{ padding: '8px 24px', borderRadius: 8, background: '#1A1A1A', color: 'white' }}
            >
              Done
            </button>
          </div>
        )}

        {/* Timeout Step */}
        {step === 'timeout' && (
          <div className="text-center">
            <div className="font-serif font-normal mb-2" style={{ fontSize: 22, color: '#1A1A1A' }}>
              Verification timed out
            </div>
            <div className="font-sans text-sm mb-6" style={{ color: '#888' }}>
              We didn't receive a response. Please try again.
            </div>
            <div className="flex gap-3 justify-center">
              <button
                onClick={onClose}
                className="font-sans text-[13px] font-medium border-none cursor-pointer"
                style={{ padding: '8px 16px', borderRadius: 8, background: 'transparent', color: '#888' }}
              >
                Cancel
              </button>
              <button
                onClick={handleRetry}
                className="font-sans text-[13px] font-semibold border-none cursor-pointer"
                style={{ padding: '8px 20px', borderRadius: 8, background: '#D4582A', color: 'white' }}
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Error Step */}
        {step === 'error' && (
          <div className="text-center">
            <div className="font-serif font-normal mb-2" style={{ fontSize: 22, color: '#1A1A1A' }}>
              Verification failed
            </div>
            <div className="font-sans text-sm mb-6" style={{ color: '#C43E00' }}>
              {errorMessage}
            </div>
            <div className="flex gap-3 justify-center">
              <button
                onClick={onClose}
                className="font-sans text-[13px] font-medium border-none cursor-pointer"
                style={{ padding: '8px 16px', borderRadius: 8, background: 'transparent', color: '#888' }}
              >
                Cancel
              </button>
              <button
                onClick={handleRetry}
                className="font-sans text-[13px] font-semibold border-none cursor-pointer"
                style={{ padding: '8px 20px', borderRadius: 8, background: '#D4582A', color: 'white' }}
              >
                Try Again
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
